/* eslint-disable no-console */
import * as Sentry from "@sentry/react";
import { VERSION } from "../config/version";
import { getLastLocation } from "../utils/links";
import BaseError from "../errors/BaseError";
import { getTenant } from "../utils/tenant";
import { browserStore } from "../utils/storageKeys";

interface LoggerType {
  init: () => void;
  info: (message: string, data?: Record<string, unknown>) => void;
  warn: (message: string, data?: Record<string, unknown>) => void;
  error: (message: string, err: Error | string | unknown, data?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    config: {
      VERSION: string;
    };
  }
}

const EDIPHI_SENTRY_DSN =
  "https://6f6fc3808de7c763667ad00dd2110c60@o4506299957510144.ingest.us.sentry.io/4508344899403776";
//const EDIPHI_DD_APP_ID = "368d2cf9-0c66-468f-9493-4c1230e679c0";
//const EDIPHI_DD_CLIENT_TOKEN = "pubb64c579b725e21b73bbf632c705c84b3";
const EDIPHI_API_URL = process.env.API_URL || "https://api.ediphi.com";
const IS_PROD = window.location.hostname === "excel.ediphi.com";
const IS_STAGE = window.location.hostname === "excel.stage.ediphi.com";
const IS_DEPLOYED = IS_PROD || IS_STAGE;

const Logger: LoggerType & typeof console = {
  ...console,

  init() {
    if (IS_DEPLOYED) {
      const userEmail = browserStore.get("userEmail") || null;
      const tenant = getTenant() ?? "excel";
      const environment = IS_PROD ? "prod" : IS_STAGE ? "stage" : "unknown";

      // Sentry Initialization
      Sentry.init({
        dsn: EDIPHI_SENTRY_DSN,
        environment,
        debug: true,
        release: VERSION,
        ignoreErrors: [
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications.",
        ],
      });
      Sentry.setTag("api_url", EDIPHI_API_URL);
      Sentry.setTag("tenant", tenant);

      if (userEmail) {
        Sentry.setUser({ email: userEmail });
      }
    }
  },

  info(message, data = {}) {
    const context = getLastLocation();
    const extra = {
      ...context,
      ...data,
    };

    if (IS_DEPLOYED) {
      Sentry.captureMessage(message, {
        level: "info",
        extra: extra,
      });
    }
    console.info(message, extra);
  },

  warn(message, data = {}) {
    const context = getLastLocation();
    const extra = {
      ...context,
      ...data,
    };

    if (IS_DEPLOYED) {
      Sentry.captureMessage(message, {
        level: "warning",
        extra: extra,
      });
    }
    console.warn(message, extra);
  },

  async error(message: string, err, data: Record<string, unknown> = {}) {
    const context = getLastLocation();
    const metadata = err instanceof BaseError ? err.info : undefined;
    // Check if the error or its cause is an OfficeExtension.Error
    let officeDebugInfo: any = undefined;
    if (err instanceof OfficeExtension.Error) {
      officeDebugInfo = err.debugInfo;
    } else if (err instanceof Error && err.cause instanceof OfficeExtension.Error) {
      officeDebugInfo = err.cause.debugInfo;
    }

    const extra = {
      userMessage: message,
      officeDebugInfo,
      ...metadata,
      ...context,
      ...data,
    };

    if (IS_DEPLOYED) {
      // Group by error name + message so the same error in different call sites becomes one issue
      const fingerprint = err instanceof Error ? [err.name, err.message] : ["Error", String(err)];

      if (err instanceof Error) {
        Sentry.captureException(err, {
          extra,
          level: "error",
          fingerprint,
        });
        console.log("Error captured by Sentry", err, extra);
      } else {
        const errorInstance = new Error(typeof err === "string" ? err : JSON.stringify(err));
        Sentry.captureException(errorInstance, {
          extra,
          level: "error",
          fingerprint,
        });
        console.log("Error captured by Sentry", err, extra);
      }
    } else {
      console.error(err, extra);
    }
  },
};

export default Logger;
